import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChatMessage from './ChatMessage';

const ChatPanel = ({ messages, onSendMessage, onAskLLM, onClearChat, isLoading, isOpen, onToggle }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    onSendMessage(input);
    setInput('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = (e) => {
    e.stopPropagation(); // Prevent toggling the panel
    onClearChat();
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="chat-panel">
      <div className="chat-panel-header">
        <div className="chat-panel-header-left" onClick={onToggle}>
          <div className="chat-panel-title">AI Feedback</div>
        </div>
        <div className="chat-panel-header-right">
          {hasMessages && (
            <button
              className="chat-panel-clear-button"
              onClick={handleClear}
              title="Clear conversation"
            >
              <FontAwesomeIcon icon="trash" />
            </button>
          )}
          <FontAwesomeIcon
            icon="chevron-down"
            className={`chat-panel-toggle ${isOpen ? 'open' : ''}`}
            onClick={onToggle}
          />
        </div>
      </div>

      {isOpen && (
        <>
          {hasMessages ? (
            <>
              <div className="chat-panel-messages">
                {messages.map((msg, idx) => (
                  <ChatMessage key={idx} {...msg} />
                ))}
                {isLoading && (
                  <div className="chat-loading">
                    <span>AI is thinking</span>
                    <div className="chat-loading-dots">
                      <div className="chat-loading-dot"></div>
                      <div className="chat-loading-dot"></div>
                      <div className="chat-loading-dot"></div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="chat-panel-input">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask a follow-up question..."
                  disabled={isLoading}
                />
                <button onClick={handleSend} disabled={!input.trim() || isLoading}>
                  Send
                </button>
              </div>
            </>
          ) : (
            <div className="chat-panel-empty">
              <p className="chat-panel-empty-text">
                Get AI feedback on your SQL solution. The AI will evaluate your query for correctness,
                efficiency, readability, and potential edge cases.
              </p>
              <button
                className="chat-panel-ask-button"
                onClick={onAskLLM}
                disabled={isLoading}
              >
                <FontAwesomeIcon icon="comments" className="mr1" />
                Ask LLM for Feedback
              </button>
              {isLoading && (
                <div className="chat-loading">
                  <span>AI is thinking</span>
                  <div className="chat-loading-dots">
                    <div className="chat-loading-dot"></div>
                    <div className="chat-loading-dot"></div>
                    <div className="chat-loading-dot"></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChatPanel;
